import { RouteComponentProps, navigate } from '@reach/router';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import React from 'react';
import { Helmet } from 'react-helmet';
import { MapDispatchToProps, MapStateToProps, connect } from 'react-redux';
import { IOption } from 'components/Select/Select';
import {
  createCompanyProject,
  createCompanyProjectFn,
  updateProject,
  updateProjectFn,
} from 'store/companies/companiesSlice';
import { RootState } from 'store/store';
import { removeDuplicateObjectsFromArray } from 'utils/happyFunctions';
import { SpaceBetween } from 'utils/style/extendable';

// import ClassicEditor from '@rastech-inc/ckeditor';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import Input from 'components/Input';
import { projectRoutesName } from 'components/MainHeader/Messages';
import notify from 'components/Notify';
import Select from 'components/Select';
import CKEditorField from 'components/CKEditorField/CKEditorField';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import {
  ICompany,
  IProject,
  IProjectStore,
  TProjects,
} from 'store/companies/companiesTypes';
import { isCompanyAdmin } from 'store/companies/companiesUtils';
import i18n, { languageDirection } from '../../../i18n';
import { getLanguageBaseOnTZ } from '../../../utils/LanguageLocalStorage';

interface IStateToProps {
  project: IProjectStore;
  company: ICompany;
  thisUser: IThisUserState;
  allProjectTags: IOption[];
}

interface IDispatchToProps {
  updateProject: (project: IProject) => ReturnType<typeof updateProjectFn>;
  createProject: (
    project: IProject
  ) => ReturnType<typeof createCompanyProjectFn>;
}

interface IOwnProps {
  companyId?: string;
  projectId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  saving: boolean;
  updateValues: boolean;
}

interface IFormValues {
  name: string;
  tags: IOption[];
  description: string;
  visibleAllTasks: boolean;
  archived: boolean;
}

class ProjectEditor extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  initialValues: IFormValues = {} as IFormValues;

  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      saving: false,
      updateValues: false,
    };
    this.initialValues = {
      name: props.project ? props.project.name : '',
      description: props.project ? props.project.description : '',
      visibleAllTasks: props.project ? props.project.visibleAllTasks : false,
      archived: props.project ? props.project.archived : false,
      tags: props.project
        ? props.project.tags
          ? (props.project.tags as IProject['tags']).map((tag) => ({
              value: `${props.projectId}_${tag}`,
              label: tag,
            }))
          : []
        : [],
    };
  }

  componentDidMount() {
    if (!isCompanyAdmin(this.props.thisUser, this.props.company)) {
      navigate('/notfound');
    }
  }
  componentDidUpdate(prevProps: IComponentProps) {
    if (prevProps.projectId !== this.props.projectId) {
      this.initialValues = {
        name: this.props.project ? this.props.project.name : '',
        description: this.props.project ? this.props.project.description : '',
        visibleAllTasks: this.props.project
          ? this.props.project.visibleAllTasks
          : false,
        archived: this.props.project ? this.props.project.archived : false,
        tags: this.props.project
          ? this.props.project.tags
            ? (this.props.project.tags as IProject['tags']).map((tag) => ({
                value: `${this.props.projectId}_${tag}`,
                label: tag,
              }))
            : []
          : [],
      };
      this.setState({ updateValues: true }, () => {
        this.setState({ updateValues: false });
      });
    }
  }

  handleSave = ({
    name,
    tags,
    description,
    visibleAllTasks,
    archived,
  }: IFormValues) => {
    if (name.length > 0) {
      this.setState({ saving: true });

      const companyId = Number(this.props.companyId);
      const projectId = Number(this.props.projectId);

      const tagLabels =
        tags.length > 0 ? tags.map((option) => option.label) : [];

      const project: IProject = {
        id: projectId,
        tags: tagLabels,
        name,
        companyId,
        description,
        visibleAllTasks,
        archived,
      };
      if (projectId) {
        // edit mode
        this.props
          .updateProject(project)
          .then(() => {
            this.setState({ saving: false });
            navigate(`/t/${companyId}/admin/project/list`);
            notify(
              i18n.t('updateProject', { ns: 'companyManagement' }) as string
            );
          })
          .catch((err: any) => {
            this.setState({ saving: false });
            display(err);
          });
      } else {
        this.props
          .createProject(project)
          .then(() => {
            this.setState({ saving: false });
            navigate(`/t/${companyId}/admin/project/list`);
            notify(
              i18n.t('createProject', {
                ns: 'companyManagement',
              }) as string
            );
          })
          .catch((err: any) => {
            this.setState({ saving: false });
            display(err);
          });
      }
    }
  };

  handleCancel = () => {
    navigate(`/t/${this.props.companyId}/admin/project/list`);
  };

  render() {
    const { project, allProjectTags } = this.props;
    const projectName = project && project.name;
    return (
      <React.Fragment>
        <Helmet>
          <title>
            {projectRoutesName(projectName ? 'editor' : 'create', projectName)}
          </title>
          <meta
            name="description"
            content={projectRoutesName(
              projectName ? 'editor' : 'create',
              projectName
            )}
          />
        </Helmet>
        <div className="section">
          <Formik
            onSubmit={this.handleSave}
            initialValues={this.initialValues}
            enableReinitialize={this.state.updateValues}
            render={(formikBag: FormikProps<IFormValues>) => (
              // @ts-expect-error JSX error
              <Form>
                <Field
                  type="text"
                  name="name"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="text"
                      id="name"
                      label={
                        i18n.t('projectName', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      placeholder={
                        i18n.t('projectName', {
                          ns: 'companyManagement',
                        }) as string
                      }
                    />
                  )}
                />

                <Field
                  type="text"
                  name="description"
                  render={() => (
                    <CKEditorField
                      id="description"
                      label={
                        i18n.t('description', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      // editor={ClassicEditor}
                      data={formikBag.values.description}
                      onChange={(data: string) => {
                        formikBag.setFieldValue('description', data);
                      }}
                      dir={languageDirection()}
                      config={{
                        toolbar: [
                          'heading',
                          'fontColor',
                          'alignment',
                          '|',
                          'bold',
                          'italic',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'indent',
                          'outdent',
                          '|',
                          'horizontalLine',
                          'link',
                          'insertTable',
                          '|',
                          'undo',
                          'redo',
                          'highlight',
                        ],
                        language: getLanguageBaseOnTZ(),
                      }}
                    />
                  )}
                />
                {/*<Field
                  type="checkbox"
                  name="visibleAllTasks"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="checkbox"
                      id="visibleAllTasks"
                      label={messages.visibleAllTasks}
                      placeholder={messages.visibleAllTasks}
                      checked={formikBag.values.visibleAllTasks}
                    />
                  )}
                />*/}

                <Field
                  type="checkbox"
                  name="archived"
                  render={(props: FieldProps) => (
                    <Input
                      {...props.field}
                      type="checkbox"
                      id="archived"
                      label={
                        i18n.t('archived', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      placeholder={
                        i18n.t('archived', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      checked={formikBag.values.archived}
                    />
                  )}
                />
                <Field
                  type="select"
                  name="allProjectTags"
                  render={() => (
                    <Select
                      options={allProjectTags}
                      values={formikBag.values.tags}
                      onChangeValues={(tags) => {
                        formikBag.setFieldValue('tags', tags);
                      }}
                      label={
                        i18n.t('tags', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      placeholder={
                        i18n.t('addTag', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      letAddChip={true}
                      multiChoice={true}
                    />
                  )}
                />
                <SpaceBetween style={{ margin: '2rem 0' }}>
                  <Button
                    color="green"
                    type="submit"
                    loading={this.state.saving}
                  >
                    {i18n.t('save', { ns: 'companyManagement' }) as string}
                  </Button>
                  <Button
                    color="red"
                    onClick={() => {
                      formikBag.handleReset();
                      this.handleCancel;
                    }}
                  >
                    {i18n.t('cancel', { ns: 'companyManagement' }) as string}
                  </Button>
                </SpaceBetween>
              </Form>
            )}
          />
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const thisUser = state.thisUser;
  const companyId = Number(props.companyId);
  const projectId = Number(props.projectId);

  const allProjects: TProjects = {};
  Object.values(state.companies.data).forEach((cmp) => {
    Object.assign(allProjects, cmp.projects);
  });
  const company = state.companies.data[companyId];
  const companyProjectsIds = Object.values(company.projects || {});
  const project = allProjects[projectId];

  let allProjectTags: IOption[] = [];
  if (allProjects && companyProjectsIds.length > 0) {
    companyProjectsIds.forEach((pId) => {
      const projectTags = allProjects[pId.id].tags;
      if (projectTags && projectTags.length > 0) {
        projectTags.forEach((tag) => {
          allProjectTags.push({ label: tag, value: `${projectId}_${tag}` });
        });
      }
    });
  }

  if (allProjectTags.length > 1) {
    allProjectTags = removeDuplicateObjectsFromArray(allProjectTags, 'label');
  }

  return { project, company, thisUser, allProjectTags };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch: ThunkDispatch<any, never, any>
) => ({
  createProject: (project: IProject) =>
    dispatch(createCompanyProject({ project })).unwrap(),
  updateProject: (project: IProject) =>
    dispatch(updateProject({ project })).unwrap(),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectEditor);
